import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { normalizeSpaces } from '../../../utils/dbText';

import { legalUrls } from '../../../constants.js/misc';
import OnboardingLayout from '../components/OnboardingLayout/OnboardingLayout';
import StepHeader from '../components/StepHeader/StepHeader';
import { TextField } from '../components/Field/Field';
import ImageDropzone from '../components/ImageDropzone/ImageDropzone';
import { normalizeImageFile } from '../onboardingV2.images';
import { trackImagePicked } from '../onboardingV2.analytics';
import { Button } from '../components/Button/Button';
import { Fields, Legal } from './steps.styles';

export const STORE_NAME_MIN_LENGTH = 4;

const schema = yup.object({
    store: yup
        .string()
        .transform((value) => normalizeSpaces(value))
        .required('SignUpV2.errors.storeRequired')
        .min(STORE_NAME_MIN_LENGTH, 'SignUpV2.errors.storeShort')
        .max(50, 'SignUpV2.errors.storeLong'),
});

/** Full name → first/last (first space). Without a last name, the first name is repeated. */
export const splitName = (fullName) => {
    const clean = String(fullName || '')
        .trim()
        .replace(/\s+/g, ' ');
    const index = clean.indexOf(' ');
    if (index === -1) return { firstName: clean, lastName: clean };
    return { firstName: clean.slice(0, index), lastName: clean.slice(index + 1) };
};

/** Digits only; drops a pasted country prefix (+57 300… / 57300…). */
export const normalizePhone = (text, dial, isoCode) => {
    let digits = String(text || '').replace(/\D/g, '');
    const max = isoCode === 'CO' ? 10 : 13;
    const pastedWithPrefix = String(text || '')
        .trim()
        .startsWith('+');
    if (dial && digits.startsWith(dial) && (pastedWithPrefix || digits.length > max)) {
        digits = digits.slice(dial.length);
    }
    return digits.slice(0, max);
};

/** '3001234567' → '300 123 4567' (Colombia only). */
export const formatPhone = (digits, isoCode) => {
    if (isoCode !== 'CO') return digits;
    return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)].filter(Boolean).join(' ');
};

/** Step 4 · tienda: store name + optional logo and banner. Saved to the draft; the account is created later. */
const StoreStep = ({ ctx }) => {
    const { t, isDemo, draft, updateDraft, goTo, trackStep, analytics, media, setLogoPhoto, setBannerPhoto } = ctx;

    const [logoError, setLogoError] = useState(null);
    const [logoBusy, setLogoBusy] = useState(false);
    const [bannerError, setBannerError] = useState(null);
    const [bannerBusy, setBannerBusy] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        setFocus,
        watch,
    } = useForm({
        resolver: yupResolver(schema),
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: { store: draft.store || '' },
    });

    const storeValue = watch('store');
    const storeField = register('store');

    React.useEffect(() => {
        const subscription = watch((values) => updateDraft({ store: values.store }));
        return () => subscription.unsubscribe();
    }, [watch, updateDraft]);

    const onValid = (values) => {
        updateDraft({ store: values.store });
        trackStep('signup_step_completed', {
            has_logo: !!(media && media.logo),
            has_banner: !!(media && media.banner),
            store_name_length: values.store.trim().length,
        });
        goTo('producto');
    };

    const onBack = () => {
        analytics.track('signup_step_back', { from_step: 'tienda' });
        goTo('categorias');
    };

    // Any picture (HEIC from an iPhone included) is converted to a format the store can show.
    const onLogoFile = async (file) => {
        setLogoError(null);
        setLogoBusy(true);
        const startedAt = Date.now();
        try {
            const ready = await normalizeImageFile(file);
            if (setLogoPhoto) setLogoPhoto(ready);
            trackImagePicked(analytics, { target: 'logo', file, ready, startedAt });
        } catch (error) {
            trackImagePicked(analytics, { target: 'logo', file, error, startedAt });
            setLogoError(
                t(error && error.message === 'too big' ? 'SignUpV2.errors.photoSize' : 'SignUpV2.errors.photoType')
            );
        } finally {
            setLogoBusy(false);
        }
    };
    const logoValue = media && media.logo ? { previewUrl: media.logo, name: media.logoName } : null;

    const onBannerFile = async (file) => {
        setBannerError(null);
        setBannerBusy(true);
        const startedAt = Date.now();
        try {
            const ready = await normalizeImageFile(file);
            if (setBannerPhoto) setBannerPhoto(ready);
            trackImagePicked(analytics, { target: 'banner', file, ready, startedAt });
        } catch (error) {
            trackImagePicked(analytics, { target: 'banner', file, error, startedAt });
            setBannerError(
                t(error && error.message === 'too big' ? 'SignUpV2.errors.photoSize' : 'SignUpV2.errors.photoType')
            );
        } finally {
            setBannerBusy(false);
        }
    };
    const bannerValue = media && media.banner ? { previewUrl: media.banner, name: media.bannerName } : null;

    return (
        <OnboardingLayout
            preview={ctx.preview}
            step={ctx.stepNumberOf('tienda')}
            total={ctx.totalSteps}
            stepKey="tienda"
            title={t('SignUpV2.tienda.title')}
            headingId="tienda-title"
            isDemo={isDemo}
            showLoginLink
            onBack={onBack}
            onSubmit={handleSubmit(onValid)}
            footer={
                <Button type="submit" iconRight="ri-arrow-right-line">
                    {t('SignUpV2.common.continue')}
                </Button>
            }
        >
            <StepHeader
                id="tienda-title"
                title={t('SignUpV2.tienda.title')}
                subtitle={t('SignUpV2.tienda.subtitle')}
            />
            <Fields>
                <TextField
                    id="signup-store"
                    label={t('SignUpV2.tienda.storeLabel')}
                    placeholder={t('SignUpV2.tienda.storePlaceholder')}
                    type="text"
                    autoComplete="organization"
                    autoCapitalize="words"
                    enterkeyhint="next"
                    maxLength={50}
                    error={errors.store ? t(errors.store.message) : null}
                    showClear={!!storeValue}
                    onClear={() => {
                        setValue('store', '', { shouldValidate: false });
                        setFocus('store');
                    }}
                    {...storeField}
                />
                <ImageDropzone
                    id="store-logo"
                    label={t('SignUpV2.tienda.logoLabel')}
                    hint={t('SignUpV2.tienda.logoHint')}
                    value={logoValue}
                    optimizing={logoBusy}
                    disabled={logoBusy}
                    error={logoError}
                    onFile={onLogoFile}
                    onRemove={() => {
                        setLogoError(null);
                        if (setLogoPhoto) setLogoPhoto(null);
                    }}
                />
                <ImageDropzone
                    id="store-banner"
                    label={t('SignUpV2.tienda.bannerLabel')}
                    hint={t('SignUpV2.tienda.bannerHint')}
                    value={bannerValue}
                    optimizing={bannerBusy}
                    disabled={bannerBusy}
                    error={bannerError}
                    onFile={onBannerFile}
                    onRemove={() => {
                        setBannerError(null);
                        if (setBannerPhoto) setBannerPhoto(null);
                    }}
                />
            </Fields>
            <Legal>
                {t('SignUpV2.tienda.legalPrefix')}{' '}
                <a href={legalUrls.terms} target="_blank" rel="noopener noreferrer">
                    {t('SignUpV2.tienda.legalTerms')}
                </a>{' '}
                {t('SignUpV2.tienda.legalAnd')}{' '}
                <a href={legalUrls.privacy} target="_blank" rel="noopener noreferrer">
                    {t('SignUpV2.tienda.legalPrivacy')}
                </a>
                {t('SignUpV2.tienda.legalEnd')}
            </Legal>
        </OnboardingLayout>
    );
};

export default StoreStep;
